import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Workspace } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Modal, ModalActions } from '../components/Modal';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { useAuth } from '../hooks/useAuth';
import { CreateWorkspaceModal } from '../components/CreateWorkspaceModal';
import { ParticleField } from '../components/ParticleField';
import {
  DashboardLayout,
  DashboardHeader,
  HeaderIdentity,
  HeaderText,
  OrbScaler,
  OrbScaleInner,
  SearchWrap,
  SearchInput,
  SearchIconSlot,
  SearchClear,
  NoResults,
  NovahCenter,
  NovahAvatarWrapper,
  OrbAmbient,
  OrbSphere,
  BlobA,
  BlobB,
  BlobC,
  OrbSheen,
  OrbRim,
  NovahAvatar,
  NovahGreeting,
  NovahSubtext,
  RecentActivity,
  ActivityTitle,
  TipCard,
  WorkspacesSection,
  WorkspaceRail,
  WorkspaceCardCell,
  WorkspaceCard,
  CardLogoTile,
  CardLogo,
  CardInitial,
  CardName,
  CardPosts,
  CardDelete,
  NewWorkspaceCard,
  BigPlus,
} from './dashboardPage/styled';

const AVATAR_IMAGE_URL = '/novah_laugh.png';

/* SVG inline y no emoji: el emoji se dibuja distinto en cada sistema operativo
   y no hereda el color del tema. */
const SearchGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ClearGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/* Gradient determinístico a partir del id del workspace — mismo string siempre
   devuelve mismo gradiente, así el fallback queda estable entre renders. */
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #6366F1, #8B5CF6)',
  'linear-gradient(135deg, #EC4899, #F43F5E)',
  'linear-gradient(135deg, #06B6D4, #3B82F6)',
  'linear-gradient(135deg, #10B981, #14B8A6)',
  'linear-gradient(135deg, #F59E0B, #EF4444)',
  'linear-gradient(135deg, #8B5CF6, #EC4899)',
  'linear-gradient(135deg, #0EA5E9, #6366F1)',
  'linear-gradient(135deg, #84CC16, #10B981)',
];

const gradientFor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CARD_GRADIENTS[h % CARD_GRADIENTS.length];
};

const TIPS = [
  'Tip: Add a logo to each workspace for branded templates',
  'Tip: Upload background photos for more diverse post images',
  'Tip: Use content mix to balance education, connection & promotion',
  'Tip: Try generating for multiple platforms at once to save time',
];

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaces, addWorkspace, removeWorkspace } = useWorkspaces();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setIsModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleWorkspaceCreated = (workspace: Workspace) => {
    addWorkspace(workspace);
    setIsModalOpen(false);
    navigate(`/workspace/${workspace.id}/generate`);
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/workspaces/${workspaceToDelete.id}`);
      removeWorkspace(workspaceToDelete.id);
      setWorkspaceToDelete(null);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      alert('Failed to delete workspace. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const totalPosts = workspaces.reduce((sum, w) => sum + (w.postsCount || 0), 0);
  const tip = TIPS[Math.floor(Date.now() / 86400000) % TIPS.length];
  const hasWorkspaces = workspaces.length > 0;

  const visibleWorkspaces = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) => w.name.toLowerCase().includes(q));
  }, [workspaces, query]);

  const isFiltering = query.trim().length > 0;

  return (
    <>
      <DashboardLayout>
        {/* ── Header: identidad + buscador en una franja. El orbe va escalado a
            76px dentro de un wrapper sin animación (ver OrbScaler). ── */}
        <DashboardHeader>
          <HeaderIdentity>
            <OrbScaler>
              <OrbScaleInner>
                <NovahAvatarWrapper>
                  <OrbAmbient />
                  <OrbSphere>
                    <BlobA $color="rgba(255, 130, 200, 0.5)" $size={100} $top="5%" $left="10%" />
                    <BlobB $color="rgba(150, 100, 255, 0.4)" $size={90} $top="35%" $left="45%" />
                    <BlobC $color="rgba(80, 200, 230, 0.35)" $size={85} $top="50%" $left="10%" />
                  </OrbSphere>
                  <OrbSheen />
                  <OrbRim />
                  <NovahAvatar>
                    <img src={AVATAR_IMAGE_URL} alt="Novah" />
                  </NovahAvatar>
                </NovahAvatarWrapper>
              </OrbScaleInner>
            </OrbScaler>

            <HeaderText>
              <NovahGreeting>Hey {firstName}!</NovahGreeting>
              <NovahSubtext>
                {!hasWorkspaces
                  ? "I'm Novah, your brand assistant. Create your first workspace to get started."
                  : isFiltering
                    ? `${visibleWorkspaces.length} of ${workspaces.length} workspace${workspaces.length !== 1 ? 's' : ''}`
                    : `${workspaces.length} workspace${workspaces.length !== 1 ? 's' : ''} · ${totalPosts} post${totalPosts !== 1 ? 's' : ''} generated`}
              </NovahSubtext>
            </HeaderText>
          </HeaderIdentity>

          {hasWorkspaces && !isModalOpen && (
            <SearchWrap>
              <SearchIconSlot>
                <SearchGlyph />
              </SearchIconSlot>
              <SearchInput
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search workspaces"
                aria-label="Search workspaces"
              />
              {isFiltering && (
                <SearchClear type="button" onClick={() => setQuery('')} aria-label="Clear search">
                  <ClearGlyph />
                </SearchClear>
              )}
            </SearchWrap>
          )}
        </DashboardHeader>

        {/* ── Novah Center ── */}
        <NovahCenter>
          <ParticleField density={0.6} />
          {isModalOpen ? (
            /* Inline wizard — replaces the grid when creating a workspace */
            <CreateWorkspaceModal
              isOpen={true}
              inline
              onClose={() => setIsModalOpen(false)}
              onCreated={handleWorkspaceCreated}
            />
          ) : (
            <>
              <WorkspacesSection>
                <WorkspaceRail>
                  {visibleWorkspaces.map((w, i) => (
                    <WorkspaceCardCell key={w.id} $i={i}>
                      <WorkspaceCard
                        onClick={() => navigate(`/workspace/${w.id}`)}
                        title={w.name}
                        aria-label={`Open ${w.name}`}
                        type="button"
                      >
                        {w.logoUrl ? (
                          <CardLogoTile>
                            <CardLogo src={w.logoUrl} alt="" />
                          </CardLogoTile>
                        ) : (
                          <CardInitial $gradient={gradientFor(w.id)}>
                            {w.name.charAt(0).toUpperCase()}
                          </CardInitial>
                        )}
                        <CardName>{w.name}</CardName>
                        <CardPosts>
                          {w.postsCount || 0} post{(w.postsCount || 0) !== 1 ? 's' : ''}
                        </CardPosts>
                      </WorkspaceCard>
                      {w.owned !== false && (
                        <CardDelete
                          data-delete
                          type="button"
                          title={`Delete ${w.name}`}
                          aria-label={`Delete ${w.name}`}
                          onClick={(e) => { e.stopPropagation(); setWorkspaceToDelete(w); }}
                        >
                          ×
                        </CardDelete>
                      )}
                    </WorkspaceCardCell>
                  ))}
                  {/* Al filtrar, "New workspace" molesta: no es un resultado. */}
                  {!isFiltering && (
                    <NewWorkspaceCard onClick={() => setIsModalOpen(true)} type="button" aria-label="Create new workspace">
                      <BigPlus>+</BigPlus>
                      New workspace
                    </NewWorkspaceCard>
                  )}
                </WorkspaceRail>

                {isFiltering && visibleWorkspaces.length === 0 && (
                  <NoResults>No workspaces match “{query.trim()}”.</NoResults>
                )}
              </WorkspacesSection>

              {!isFiltering && (
                <RecentActivity>
                  <ActivityTitle>Daily tip</ActivityTitle>
                  <TipCard>{tip}</TipCard>
                </RecentActivity>
              )}
            </>
          )}
        </NovahCenter>

      </DashboardLayout>


      <Modal
        isOpen={!!workspaceToDelete}
        onClose={() => setWorkspaceToDelete(null)}
        title="Delete Workspace?"
        titleVariant="error"
        closeOnOverlayClick={!isDeleting}
        closeOnEscape={!isDeleting}
      >
        <p style={{ color: '#525252', marginBottom: '16px', lineHeight: 1.5 }}>
          Are you sure you want to delete &quot;{workspaceToDelete?.name}&quot;? This will permanently delete:
        </p>
        <ul style={{ marginTop: '12px', paddingLeft: '20px', color: '#525252' }}>
          <li>All brand information and research</li>
          <li>All brand assets and images</li>
          <li>All generated posts ({workspaceToDelete?.postsCount || 0} posts)</li>
          <li>All export history</li>
        </ul>
        <strong style={{ display: 'block', marginTop: '12px', color: '#EF4444' }}>
          This action cannot be undone.
        </strong>
        <ModalActions>
          <Button type="button" $variant="outline" onClick={() => setWorkspaceToDelete(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleDeleteWorkspace} disabled={isDeleting} $variant="danger">
            {isDeleting ? 'Deleting...' : 'Delete Workspace'}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
